import {
  useLayoutContext
} from "./chunk-HFINPXUM.mjs";

// src/sidepanel.tsx
import * as React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ArrowLeftIcon } from "@clara/icons";
import { getValidChildren } from "@clara/react-utils";
import {
  Flex,
  CloseButton,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  DrawerHeader
} from "@clara/ui";
import { runIfFn } from "@clara/utils";
import { jsx, jsxs } from "react/jsx-runtime";
function SidePanel(props) {
  var _a, _b, _c, _d, _e;
  const { sidePanel: sider } = useLayoutContext();
  const drawerProps = sider.getDrawerProps(props);
  const sharedProps = {
    onOpen: sider.onOpen,
    onClose: sider.onClose,
    next: sider.next,
    prev: sider.prev,
    current: sider.current,
    ...drawerProps
  };
  const clonedPanel = getValidChildren((_a = sider.current) == null ? void 0 : _a.element).map(
    (child) => React.cloneElement(child, sharedProps)
  );
  const header = runIfFn(
    (_c = (_b = sider.current) == null ? void 0 : _b.header) != null ? _c : /* @__PURE__ */ jsx(SidePanelHeader, { headline: "" }),
    {}
  );
  const children = getValidChildren((_d = sider.current) == null ? void 0 : _d.footer).map(
    (child) => React.cloneElement(child, sharedProps)
  );
  const footer = children.length > 0 ? children : runIfFn((_e = sider.current) == null ? void 0 : _e.footer, sharedProps);
  const Wrapper = React.useMemo(
    () => sider.isScrollAnimated === false ? (props2) => /* @__PURE__ */ jsx(
      Box,
      {
        sx: () => ({
          overflowY: "auto",
          flex: 1
        }),
        ...props2
      }
    ) : ({ children: children2 }) => /* @__PURE__ */ jsx(React.Fragment, { children: children2 }),
    [sider.isScrollAnimated]
  );
  return /* @__PURE__ */ jsxs(Drawer, { ...drawerProps, children: [
    /* @__PURE__ */ jsxs(Wrapper, { children: [
      header,
      /* @__PURE__ */ jsx(
        DrawerBody,
        {
          ref: sider.bodyRef,
          p: 3,
          ...sider.isScrollAnimated && {
            onScroll: sider.onScroll,
            overflow: "auto"
          },
          sx: { color: (theme) => theme.palette.v3.content.medium_emphasis },
          children: clonedPanel
        }
      )
    ] }),
    footer && /* @__PURE__ */ jsx(
      DrawerFooter,
      {
        sx: (theme) => ({
          position: "relative",
          justifyContent: "center",
          "&:after": {
            content: "''",
            display: "block",
            position: "absolute",
            top: theme.spacing(-1.5),
            left: 0,
            height: theme.spacing(1.5),
            width: "100%",
            background: "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 63.02%)"
          }
        }),
        children: footer
      }
    )
  ] });
}
function SidePanelBackButton(props) {
  const { onClick: onClickProp, ...rest } = props;
  return /* @__PURE__ */ jsx(
    CloseButton,
    {
      onClick: (event) => {
        event.stopPropagation();
        onClickProp == null ? void 0 : onClickProp(event);
      },
      ...rest,
      children: /* @__PURE__ */ jsx(ArrowLeftIcon, {})
    }
  );
}
var SidePanelHeader = ({
  variant = "default",
  headline,
  tagline
}) => {
  const { sidePanel: sider } = useLayoutContext();
  const withBackButton = sider.index > 0;
  React.useEffect(() => {
    if (variant === "default")
      sider.setScrollAnimated.on();
    else
      sider.setScrollAnimated.off();
  }, [variant]);
  return /* @__PURE__ */ jsxs(
    DrawerHeader,
    {
      sx: (theme) => ({
        ...getStyles({
          theme,
          variant,
          isScrolling: sider.isScrolling,
          withBackButton
        })
      }),
      children: [
        /* @__PURE__ */ jsxs(
          Stack,
          {
            direction: "row",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: 36,
            height: "auto",
            overflow: "hidden",
            position: "relative",
            width: "100%",
            children: [
              /* @__PURE__ */ jsxs(Flex, { align: "center", children: [
                withBackButton && /* @__PURE__ */ jsx(
                  SidePanelBackButton,
                  {
                    onClick: () => {
                      sider.prev();
                    }
                  }
                ),
                /* @__PURE__ */ jsx(Typography, { ml: withBackButton ? 1 : 0, mr: 2, variant: "h3", children: headline })
              ] }),
              /* @__PURE__ */ jsx(DrawerCloseButton, {})
            ]
          }
        ),
        variant === "alternate" && tagline && /* @__PURE__ */ jsx(
          Typography,
          {
            variant: "body1",
            color: (theme) => theme.palette.v3.content.medium_emphasis,
            mt: 0.5,
            children: tagline
          }
        )
      ]
    }
  );
};
function getStyles({
  theme,
  variant = "default",
  isScrolling,
  withBackButton
}) {
  var _a;
  const styles = {
    default: {
      transition: `box-shadow ${theme.transitions.duration.short} ${theme.transitions.easing.easeIn}`,
      boxShadow: isScrolling ? theme.elevations.center.sm : "none",
      pl: withBackButton ? "20px" : 3,
      pr: "20px"
    },
    alternate: {}
  };
  const defaults = {
    flexDirection: "column",
    alignItems: "flex-start",
    height: "unset"
  };
  return Object.assign(defaults, (_a = styles == null ? void 0 : styles[variant]) != null ? _a : {});
}
function SidePanelFooter() {
}

export {
  SidePanel,
  SidePanelHeader,
  SidePanelFooter
};
